import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { Season } from "../models/leagueInterfaces";
import { toast } from "react-toastify";
import {
  getAllGroupedTeams,
  getCurrentSeason,
  getGroups,
  getMatchSetups,
} from "../services/publicService";
import { MatchSetupConfig } from "../models/match";
import { Group, GroupedTeam, PublicMember, PublicTeam } from "../models/groupedTeams";
import { storeVersion, migrate, onRehydrateStorage } from "./versioning";

const defaultValues = {
  version: storeVersion,
  currentSeason: null,
  matchSetups: [],
  isBusy: false,
  allGroupedTeams: [],
  allTeams: [],
  allPlayers: [],
};

interface PublicStore {
  version: number;
  currentSeason: Season | null;
  matchSetups: MatchSetupConfig[];
  isBusy: boolean;
  allGroupedTeams: GroupedTeam[];
  allTeams: PublicTeam[];
  allPlayers: PublicMember[];
  allGroups: Group[];
  
  getCurrentSeason: () => Promise<void>;
  getMatchSetups: () => Promise<void>;
  getAllGroupedTeams: () => Promise<void>;
  getAllGroups: () => Promise<void>;
}

export const usePublicStore = create<PublicStore>()(
  devtools(
    persist(
      (set) => ({
        ...defaultValues,
        getCurrentSeason: async () => {
          try {
            set({ isBusy: true });
            const response = await getCurrentSeason();
            if (response.code && response.code === 10000) {
              set({ currentSeason: response.result, isBusy: false });
              return;
            } else {
              set({ currentSeason: null, isBusy: false });
              console.error("Error fetching season:", response);
              toast.error(response.message);
            }
          } catch (error) {
            set({ currentSeason: null, isBusy: false });
            console.error("Error fetching season:", error);
            toast.error("Error fetching season details.");
          } finally {
            set({ isBusy: false });
          }
        },
        getMatchSetups: async () => {
          try {
            set({ isBusy: true });
            const response = await getMatchSetups();
            if (response.statusCode && response.statusCode === 200) {
              set({ matchSetups: response.result, isBusy: false });
              return;
            } else {
              set({ matchSetups: [], isBusy: false });
              console.error("Error fetching match setups:", response);
              toast.error(response.message);
            }
          } catch (error) {
            set({ matchSetups: [], isBusy: false });
            console.error("Error fetching match setups:", error);
            toast.error("Error fetching match setups.");
          } finally {
            set({ isBusy: false });
          }
        },
        getAllGroupedTeams: async () => {
          try {
            set({ isBusy: true });

            const response = await getAllGroupedTeams();
            if (response.code && response.code === 10002) {
              const grpedTeams = response.result as GroupedTeam[];
              const allTeams: PublicTeam[] = [];
              const allPlayers: PublicMember[] = [];
              grpedTeams.forEach((grp) => {
                grp.teams.forEach((team) => {
                  allTeams.push(team);
                  team.members.forEach((member) => {
                    allPlayers.push(member);
                  });
                });
              });
              set({ allTeams, allPlayers });
              set({ allGroupedTeams: response.result, isBusy: false });

              return;
            } else {
              console.error("Error fetching teams:", response);
              toast.error(response.message);
            }
            set({ isBusy: false });
          } catch (error) {
            console.error("Error fetching teams:", error);
            toast.error(
              "Error fetching teams... Please check your network connectivity."
            );
            set({ isBusy: false });
          } finally {
            set({ isBusy: false });
          }
        },
        getAllGroups: async () => {
          try {
            set({ isBusy: true });
            const response = await getGroups();
            if (response.code && response.code === 3108) {
              set({ allGroups: response.result, isBusy: false });
              return;
            } else {
              set({ allGroups: [], isBusy: false });
              console.error("Error fetching groups:", response);
              toast.error(response.message);
            }
          } catch (error) {
            set({ allGroups: [], isBusy: false });
            console.error("Error fetching groups:", error);
            toast.error("Error fetching groups.");
          } finally {
            set({ isBusy: false });
          }
        }
      }),
      {
        name: "allDataStore",
        storage: createJSONStorage(() => localStorage),
        onRehydrateStorage: onRehydrateStorage(defaultValues),
        migrate: migrate(defaultValues),
      }
    ),
    { name: "publicStore", enabled: process.env.NODE_ENV === "development" }
  )
);
